/**
 * MVP Configuration - Controls which features are enabled for launch
 * Set to false to hide/disable incomplete features
 */

export const MVP_CONFIG = {
  // Core Features (must work for MVP)
  auth: true,
  dashboard: true,
  profile: true,
  
  // Navigation Features
  navigation: {
    // Essential navigation
    dashboard: true,
    learn: true,
    next: true,
    community: true,
    more: true,
    
    // Secondary features - disable if incomplete
    guides: true,
    milestones: true,
    library: true,
    reflections: true,
    chat: true,
    analytics: false, // Disable if incomplete
    achievements: true,
    upcoming: true,
    connections: true,
  },
  
  // Course Features
  courses: {
    enabled: true,
    showIncompleteAsComingSoon: true, // Show but mark as coming soon
    allowPreview: false, // Incomplete courses stay visible, but cannot be started
    /**
     * Free journeys that are completable after launch scaffolding.
     * Other published Sanity paths stay listed and gated.
     */
    shippableSlugs: ['new-to-america', 'budgeting-start', 'health-insurance'],
  },
  
  // Payment Features
  payment: {
    enabled: true,
    showComingSoonIfNotReady: true, // Show "Coming Soon" if payment not ready
    allowTestMode: true, // Allow test mode for MVP
  },
  
  // Premium Features
  premium: {
    enabled: true,
    showUpgradePrompts: true,
    allowFreeTier: true,
  },
  
  // Advanced Features (disable for MVP if incomplete)
  advanced: {
    aiRecommendations: true,
    proactiveGuidance: true,
    analytics: false,
    adminPanel: false,
    contentManagement: false,
  },
  
  // Feature Flags for specific incomplete features
  features: {
    lifetimeSubscription: false, // Disable if not ready
    familySharing: false,
    consultations: false,
    communityForum: false,
    videoLessons: true,
    downloadableResources: true,
  }
};

// Helper function to check if a feature is enabled
export const isFeatureEnabled = (featurePath: string): boolean => {
  const parts = featurePath.split('.');
  let current: any = MVP_CONFIG;
  
  for (const part of parts) {
    if (current[part] === undefined) {
      return false;
    }
    current = current[part];
  }
  
  return current === true;
};

// Helper to check if route should be accessible
export const isRouteEnabled = (route: string): boolean => {
  const routeMap: Record<string, string> = {
    '/dashboard': 'navigation.dashboard',
    '/learn': 'navigation.learn',
    '/next': 'navigation.next',
    '/community': 'navigation.community',
    '/more': 'navigation.more',
    '/guides': 'navigation.guides',
    '/milestones': 'navigation.milestones',
    '/library': 'navigation.library',
    '/reflections': 'navigation.reflections',
    '/chat': 'navigation.chat',
    '/analytics': 'navigation.analytics',
    '/achievements': 'navigation.achievements',
    '/upcoming': 'navigation.upcoming',
    '/connections': 'navigation.connections',
  };
  
  const featurePath = routeMap[route];
  if (!featurePath) return true; // Allow unknown routes (might be dynamic)
  
  return isFeatureEnabled(featurePath);
};

